// ================ Action types ================ //

import { getTranslation, putTranslation } from '../util/api';
import { addTranslationEntities } from './marketplaceData.duck';

export const FETCH_TRANSLATIONS_REQUEST = 'app/translations/FETCH_TRANSLATIONS_REQUEST';
export const FETCH_TRANSLATIONS_SUCCESS = 'app/translations/FETCH_TRANSLATIONS_SUCCESS';
export const FETCH_TRANSLATIONS_ERROR = 'app/translations/FETCH_TRANSLATIONS_ERROR';

export const SAVE_TRANSLATIONS_REQUEST = 'app/translations/SAVE_TRANSLATIONS_REQUEST';
export const SAVE_TRANSLATIONS_SUCCESS = 'app/translations/SAVE_TRANSLATIONS_SUCCESS';
export const SAVE_TRANSLATIONS_ERROR = 'app/translations/SAVE_TRANSLATIONS_ERROR';

// ================ Reducer ================ //

/** @type {import('../../types/vansite').VanSite.State.Translations} */
const initialState = {
  fetchTranslationsInProgress: false,
  fetchTranslationsError: null,
  saveTranslationsInProgress: false,
  saveTranslationsError: null,
};

/**
 *
 * @param state
 * @param action
 */
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_TRANSLATIONS_REQUEST:
      return { ...state, fetchTranslationsInProgress: true, fetchTranslationsError: null };
    case FETCH_TRANSLATIONS_SUCCESS:
      return { ...state, fetchTranslationsInProgress: false };
    case FETCH_TRANSLATIONS_ERROR:
      return { ...state, fetchTranslationsError: payload, fetchTranslationsInProgress: false };

    case SAVE_TRANSLATIONS_REQUEST:
      return { ...state, saveTranslationsInProgress: true, saveTranslationsError: null };
    case SAVE_TRANSLATIONS_SUCCESS:
      return { ...state, saveTranslationsInProgress: false };
    case SAVE_TRANSLATIONS_ERROR:
      return { ...state, saveTranslationsError: payload, saveTranslationsInProgress: false };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchTranslationsRequest = () => ({ type: FETCH_TRANSLATIONS_REQUEST });
export const fetchTranslationsSuccess = () => ({ type: FETCH_TRANSLATIONS_SUCCESS });
export const fetchTranslationsError = (payload) => ({ type: FETCH_TRANSLATIONS_ERROR, payload });

export const saveTranslationsRequest = () => ({ type: SAVE_TRANSLATIONS_REQUEST });
export const saveTranslationsSuccess = () => ({ type: SAVE_TRANSLATIONS_SUCCESS });
export const saveTranslationsError = (payload) => ({ type: SAVE_TRANSLATIONS_ERROR, payload });

// ================ Helpers ================ //

/**
 * Sanitize the `items` payload for `fetchTranslations`. Each entry's `ids` are
 * narrowed to non-empty strings and deduplicated; entries left without any
 * valid id are dropped entirely. A single null/undefined id would otherwise
 * reach the backend as a null DynamoDB key and reject the whole BatchGet.
 *
 * @param {object} items - Map of entity type to `{ ids, query? }`.
 * @returns {object} Sanitized items map.
 */
export const sanitizeTranslationItems = (items) => {
  const sanitized = {};
  for (const key of Object.keys(items || {})) {
    const entry = items[key] || {};
    const ids = [
      ...new Set((entry.ids || []).filter((id) => typeof id === 'string' && id.length > 0)),
    ];
    if (ids.length > 0) {
      sanitized[key] = { ...entry, ids };
    }
  }
  return sanitized;
};

// ================ Thunks ================ //

export const fetchTranslations = (items, locale) => async (dispatch) => {
  const sanitizedItems = sanitizeTranslationItems(items);
  if (Object.keys(sanitizedItems).length === 0) {
    return [];
  }
  try {
    dispatch(fetchTranslationsRequest());
    const response = await getTranslation({ items: sanitizedItems, locale });
    dispatch(addTranslationEntities(response));
    dispatch(fetchTranslationsSuccess());
    return response;
  } catch (e) {
    dispatch(fetchTranslationsError(e));
  }
};

export const saveTranslations = (query) => async (dispatch) => {
  try {
    dispatch(saveTranslationsRequest());
    const response = await putTranslation(query);
    dispatch(saveTranslationsSuccess());
    return response;
  } catch (e) {
    dispatch(saveTranslationsError(e));
  }
};
